import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/api';

import LucideIcon from './LucideIcon';
import WhiskeyLogo from './layout/WhiskeyLogo';
import StreakChip from './gamification/StreakChip';
import ChecklistCard from './dashboard/ChecklistCard';
import RankRing from './gamification/RankRing';
import { getRankName } from '../utils/iconMapper';

interface UserProgress {
  level: number;
  points: number;
  streak: number;
  badges: string[];
  username: string;
  nextLevelPoints: number;
  challengesCompleted: number;
  questsCompleted?: number;
  questLevelsCompleted?: number;
  topicProgress: {
    [topicId: string]: {
      completed: number;
      total: number;
      completedChallenges: string[];
    };
  };
}

interface Challenge {
  id: string;
  title: string;
  description: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  category: string;
  points: number;
  estimatedTime?: string;
  scenario?: string;
  objective?: string;
}

// Applied-practice topics beyond Basic Prompt Engineering -- see
// frontend/src/config/topics.ts for the canonical list. Kept as a separate
// literal here (not imported) because this view needs each topic's live
// progress merged in.
const OTHER_TOPICS = [
  { id: 'writing-editing', name: 'Writing & Editing', icon: 'topic-writing-editing' },
  { id: 'summarizing-research', name: 'Summarizing & Research Synthesis', icon: 'topic-summarizing-research' },
  { id: 'data-analysis', name: 'Data Analysis & Structured Output', icon: 'topic-data-analysis' },
  { id: 'coding-debugging', name: 'Coding & Debugging Assistance', icon: 'topic-coding-debugging' },
  { id: 'tutoring-explaining', name: 'Tutoring & Explaining Concepts', icon: 'topic-tutoring-explaining' },
  { id: 'customer-communication', name: 'Customer & Client Communication', icon: 'topic-customer-communication' },
  { id: 'planning-decisions', name: 'Planning & Decision Support', icon: 'topic-planning-decisions' },
  { id: 'creative-ideation', name: 'Creative & Content Ideation', icon: 'topic-creative-ideation' },
  { id: 'agent-design', name: 'Designing AI Agents & Workflows', icon: 'topic-agent-design' },
];

// Mirrors backend/src/index.js's calculateLevel/calculateNextLevelPoints
// thresholds -- needed here only to compute RankRing's progress-to-next-level
// percentage from points, since the API returns points/nextLevelPoints, not
// a ready-made percentage.
const LEVEL_THRESHOLDS = [0, 1000, 2500, 5000, 10000, 20000];

const PRACTICE_SHORTCUTS = [
  { to: '/frameworks', icon: 'topic-agent-design', title: 'Agent Frameworks', description: 'LangGraph, CrewAI, OpenAI Agents, ADK & Strands' },
  { to: '/production', icon: 'topic-coding-debugging', title: 'Production & Observability', description: 'Tracing, evaluation, routing & caching' },
  { to: '/review', icon: 'Zap', title: 'Spaced Review', description: 'Reinforce techniques and keep your streak alive' },
  { to: '/prompt-quest', icon: 'quest', title: 'Story Missions', description: 'Story-based challenges across 5 quests' },
  { to: '/challenges', icon: 'quest-trials', title: 'Foundations & Tasks', description: 'Context optimization & prompt patterns' },
  { to: '/gamification', icon: 'gamification', title: 'Badges & Rewards', description: 'Track achievements and milestones' },
];

const Dashboard: React.FC = () => {
  const { user } = useAuth();

  const [progress, setProgress] = useState<UserProgress>({
    level: 1,
    points: 100,
    streak: 1,
    badges: [],
    username: user?.firstName || 'User',
    nextLevelPoints: 900,
    challengesCompleted: 1,
    questsCompleted: 0,
    questLevelsCompleted: 0,
    topicProgress: {
      'basic-prompt-engineering': { completed: 0, total: 14, completedChallenges: [] },
    },
  });

  const [nextChallenge, setNextChallenge] = useState<Challenge | null>(null);
  // Due-review count for the banner below -- best-effort: a learner who has
  // never completed a lesson, or whose review fetch fails, just sees no
  // banner rather than an error, since this is a nicety over the main flow.
  const [reviewDueCount, setReviewDueCount] = useState(0);
  // Distinguishes "the API call failed" from "the user has genuinely finished
  // every recommended challenge" -- these need different hero messaging below,
  // since telling someone "track complete!" because of a network error would
  // be actively misleading.
  const [recommendedError, setRecommendedError] = useState(false);

  const loadRecommendedContent = useCallback(async (currentProgress: UserProgress) => {
    try {
      const response = await API.get('/challenges/recommended');
      const uncompleted = (response || []).filter(
        (c: Challenge) => !currentProgress.topicProgress?.['basic-prompt-engineering']?.completedChallenges?.includes(c.id)
      );
      setNextChallenge(uncompleted[0] || null);
      setRecommendedError(false);
    } catch (error) {
      setNextChallenge(null);
      setRecommendedError(true);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const response = await API.get('/progress');
      if (response && response.length > 0) {
        const progressData = response[0];
        progressData.username = user?.firstName || progressData.username || 'User';
        setProgress(progressData);
        loadRecommendedContent(progressData);
      } else {
        loadRecommendedContent(progress);
      }
    } catch (error) {
      loadRecommendedContent(progress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.firstName, loadRecommendedContent]);

  const loadReviewDueCount = useCallback(async () => {
    try {
      const data = await API.get('/review');
      setReviewDueCount(data?.due ? (data.dueCount || 0) : 0);
    } catch (error) {
      setReviewDueCount(0);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadReviewDueCount();

    const handleChallengeCompleted = () => {
      setTimeout(() => { loadData(); loadReviewDueCount(); }, 1000);
    };
    window.addEventListener('challengeCompleted', handleChallengeCompleted as EventListener);
    return () => window.removeEventListener('challengeCompleted', handleChallengeCompleted as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadData, loadReviewDueCount]);

  window.refreshDashboard = loadData;

  const basicProgress = progress.topicProgress?.['basic-prompt-engineering'] || { completed: 0, total: 14, completedChallenges: [] };
  const nextTopic = OTHER_TOPICS.find((t) => {
    const tp = progress.topicProgress?.[t.id];
    return !tp || tp.completed < tp.total;
  });
  // A truly brand-new signup, not a returning user with a lapsed streak --
  // challengesCompleted is a lifetime counter, so this only reads true once,
  // before their very first completed challenge.
  const isFirstVisit = progress.challengesCompleted === 0;
  const levelFloor = LEVEL_THRESHOLDS[progress.level - 1] ?? 0;
  const levelCeiling = progress.nextLevelPoints || LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1];
  const rankProgressPercent = levelCeiling > levelFloor
    ? Math.round(((progress.points - levelFloor) / (levelCeiling - levelFloor)) * 100)
    : 100;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
          {isFirstVisit ? (
            <>Welcome, <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{progress.username}</span> &mdash; let's write your first prompt.</>
          ) : (
            <>Welcome back, <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{progress.username}</span> &mdash; ready to keep your streak going?</>
          )}
        </span>
        <StreakChip days={progress.streak} />
      </div>

      {/* A streak that rewards retrieving a technique days later, not just
          opening the app -- see docs/build-plan/08-lesson-design.md finding 3.
          Sits above the hero rather than replacing it: a returning learner
          clears the review queue first, a new one still meets a single
          dominant next action. */}
      {reviewDueCount > 0 && (
        <Link
          to="/review"
          className="ws-fade-up btn-live"
          style={{
            textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
            background: 'var(--ember-soft)', border: '1px solid var(--ember)', borderRadius: 14, padding: '14px 20px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ color: 'var(--ember-strong)', display: 'flex' }}><LucideIcon name="RotateCw" size={18} /></span>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              {reviewDueCount === 1 ? 'You have 1 technique due for review' : `You have ${reviewDueCount} techniques due for review`}
            </span>
          </div>
          <span className="ws-display" style={{ fontSize: 13, fontWeight: 700, color: 'var(--ember-strong)', display: 'flex', alignItems: 'center', gap: 4 }}>
            Review now
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="var(--ember-strong)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M7 4l6 6-6 6" /></svg>
          </span>
        </Link>
      )}

      {/* Hero: single dominant next action */}
      <div
        className="ws-fade-up ws-dashboard-hero"
        style={{
          display: 'flex', background: 'var(--surface)',
          border: isFirstVisit ? '2px dashed var(--ember)' : '1px solid var(--border)',
          borderRadius: 20, boxShadow: '0 1px 2px oklch(0.2 0.02 260 / 0.06), 0 12px 32px oklch(0.2 0.02 260 / 0.05)', overflow: 'hidden',
        }}
      >
        <div style={{ flex: 1, padding: '36px 40px', display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          {nextChallenge ? (
            <>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ember-strong)' }}>
                {isFirstVisit ? 'Welcome to Whiskey Sensei' : 'Continue your training'}
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span style={{ alignSelf: 'flex-start', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: 999, padding: '4px 12px' }}>
                  Basic Prompt Engineering
                </span>
                <h1 className="ws-display" style={{ margin: 0, fontSize: 32, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-primary)', lineHeight: 1.15 }}>
                  {nextChallenge.title}
                </h1>
                <p style={{ margin: 0, fontSize: 14.5, color: 'var(--text-secondary)', lineHeight: 1.55, maxWidth: 480 }}>
                  {nextChallenge.description}
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420, marginTop: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--text-tertiary)', fontWeight: 600 }}>
                  <span>Challenge {basicProgress.completed + 1} of {basicProgress.total}</span>
                  <span>{Math.round((basicProgress.completed / basicProgress.total) * 100)}%</span>
                </div>
                <div style={{ width: '100%', height: 8, borderRadius: 999, background: 'var(--surface-sunken)', border: '1px solid var(--border)', overflow: 'hidden' }}>
                  <div className="ws-fill-once" style={{ width: `${(basicProgress.completed / basicProgress.total) * 100}%`, height: '100%', borderRadius: 999, background: 'var(--ember)' }} />
                </div>
              </div>
              <Link
                to="/challenges?topic=basic-prompt-engineering"
                className="btn-live"
                style={{ alignSelf: 'flex-start', marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--ember)', color: 'white', fontWeight: 700, fontSize: 14.5, padding: '12px 26px', borderRadius: 10, textDecoration: 'none' }}
              >
                {isFirstVisit ? 'Start Lesson' : 'Continue Lesson'}
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M7 4l6 6-6 6" /></svg>
              </Link>
            </>
          ) : recommendedError ? (
            <>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ember-strong)' }}>
                Ready when you are
              </span>
              <h1 className="ws-display" style={{ margin: 0, fontSize: 32, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-primary)', lineHeight: 1.15 }}>
                Start Basic Prompt Engineering
              </h1>
              <p style={{ margin: 0, fontSize: 14.5, color: 'var(--text-secondary)', lineHeight: 1.55, maxWidth: 480 }}>
                We couldn't load your saved progress just now &mdash; pick up your training here.
              </p>
              <Link
                to="/challenges?topic=basic-prompt-engineering"
                className="btn-live"
                style={{ alignSelf: 'flex-start', marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--ember)', color: 'white', fontWeight: 700, fontSize: 14.5, padding: '12px 26px', borderRadius: 10, textDecoration: 'none' }}
              >
                Go to Challenges
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M7 4l6 6-6 6" /></svg>
              </Link>
            </>
          ) : nextTopic ? (
            <>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ember-strong)' }}>
                Basic Prompt Engineering complete &mdash; keep going
              </span>
              <h1 className="ws-display" style={{ margin: 0, fontSize: 32, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-primary)', lineHeight: 1.15 }}>
                Practice: {nextTopic.name}
              </h1>
              <p style={{ margin: 0, fontSize: 14.5, color: 'var(--text-secondary)', lineHeight: 1.55, maxWidth: 480 }}>
                Generate a new applied-practice challenge in this track and put your prompt-engineering skills to work.
              </p>
              <Link
                to={`/challenges?topic=${nextTopic.id}`}
                className="btn-live"
                style={{ alignSelf: 'flex-start', marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--ember)', color: 'white', fontWeight: 700, fontSize: 14.5, padding: '12px 26px', borderRadius: 10, textDecoration: 'none' }}
              >
                Generate Challenge
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M7 4l6 6-6 6" /></svg>
              </Link>
            </>
          ) : (
            <>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ember-strong)' }}>
                All caught up
              </span>
              <h1 className="ws-display" style={{ margin: 0, fontSize: 32, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-primary)', lineHeight: 1.15 }}>
                You've completed every track
              </h1>
              <p style={{ margin: 0, fontSize: 14.5, color: 'var(--text-secondary)', lineHeight: 1.55, maxWidth: 480 }}>
                Revisit the Playground to keep sharpening your prompts, or check the leaderboard to see how you stack up.
              </p>
              <Link
                to="/playground"
                className="btn-live"
                style={{ alignSelf: 'flex-start', marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--ember)', color: 'white', fontWeight: 700, fontSize: 14.5, padding: '12px 26px', borderRadius: 10, textDecoration: 'none' }}
              >
                Open Playground
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M7 4l6 6-6 6" /></svg>
              </Link>
            </>
          )}
        </div>

        <div className="ws-dashboard-hero-side" style={{ width: 280, flex: '0 0 280px', background: 'var(--ember-soft)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 32 }}>
          <div style={{ width: 118, height: 118, borderRadius: '50%', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px oklch(0.6 0.19 38 / 0.18)' }}>
            <WhiskeyLogo size={70} />
          </div>
          <p className="ws-display" style={{ margin: 0, textAlign: 'center', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.4, maxWidth: 190 }}>
            {isFirstVisit
              ? <>&ldquo;Ruff! I'm Whiskey &mdash; let's get your first win on the board.&rdquo;</>
              : <>&ldquo;Ruff! Nice pace &mdash; let's keep that streak going.&rdquo;</>}
          </p>
          <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>&mdash; Whiskey</span>
        </div>
      </div>

      {isFirstVisit && (
        <ChecklistCard
          title="Get set up"
          items={[
            { label: 'Complete your first mission', to: '/challenges?topic=basic-prompt-engineering', checked: false },
            { label: 'Try the Playground', to: '/playground', checked: false },
            { label: 'Explore your first story mission', to: '/prompt-quest', checked: false },
          ]}
        />
      )}

      {/* Slim secondary stat strip */}
      <div className="ws-fade-up ws-dashboard-stats" style={{ display: 'flex', alignItems: 'stretch', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '6px 8px', animationDelay: '60ms' }}>
        <div className="ws-dashboard-stat-item" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px' }}>
          <LucideIcon name="points" size={17} />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span className="ws-display" style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{progress.points}</span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Credits</span>
          </div>
        </div>
        <div className="ws-dashboard-stat-divider" style={{ width: 1, background: 'var(--border)', margin: '6px 0' }} />
        <div className="ws-dashboard-stat-item" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, padding: '6px 18px' }}>
          <RankRing level={progress.level} progress={rankProgressPercent} size={38} />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span className="ws-display" style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{getRankName(progress.level)}</span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{rankProgressPercent}% to next</span>
          </div>
        </div>
        <div className="ws-dashboard-stat-divider" style={{ width: 1, background: 'var(--border)', margin: '6px 0' }} />
        <div className="ws-dashboard-stat-item" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px' }}>
          <LucideIcon name="missions" size={17} />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span className="ws-display" style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{progress.challengesCompleted}</span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Missions completed</span>
          </div>
        </div>
        <div className="ws-dashboard-stat-divider" style={{ width: 1, background: 'var(--border)', margin: '6px 0' }} />
        <div className="ws-dashboard-stat-item" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px' }}>
          <LucideIcon name="quest" size={17} />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span className="ws-display" style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{progress.questsCompleted || 0}/5</span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Quests</span>
          </div>
        </div>
        <div className="ws-dashboard-stat-divider" style={{ width: 1, background: 'var(--border)', margin: '6px 0' }} />
        <div className="ws-dashboard-stat-item" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px' }}>
          <LucideIcon name="quest-trials" size={17} />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span className="ws-display" style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{progress.questLevelsCompleted || 0}/50</span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Trials mastered</span>
          </div>
        </div>
      </div>

      {/* More ways to practice (merged Continue Training + Command Center) */}
      <div className="ws-fade-up" style={{ display: 'flex', flexDirection: 'column', gap: 14, animationDelay: '120ms' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <h2 className="ws-display" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>More ways to practice</h2>
          <Link to="/challenges" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ember-strong)', textDecoration: 'none' }}>View all</Link>
        </div>
        <div className="ws-dashboard-practice-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 14 }}>
          {PRACTICE_SHORTCUTS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="card-live"
              style={{ display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 18, textDecoration: 'none' }}
            >
              <span style={{ color: 'var(--ember-strong)' }}><LucideIcon name={item.icon} size={20} /></span>
              <span className="ws-display" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{item.title}</span>
              <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>{item.description}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
